import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsInstance } from "echarts-for-react";
import type { WeekDetails } from "../../../types/weekly-cost-report.types";
import type { CallbackDataParams } from "echarts/types/dist/shared";
import type { Top5Customers } from "../../../types/weekly-cost-report.types";
import {
  getCustomerStatusColors,
  buildScatterChartData,
} from "../../../utils/weekly-cost-report/weeklyCostChart.utils";
import { buildTooltipForCharts } from "../../../utils/shared/costTooltip.utils";
import { CUSTOMER_STATUS_COLORS } from "@/features/cost/constants/weekly-cost.constants";
import { ConversionToK } from "../../../utils/shared/costCalculations.utils";
import theme, { CHART, ACCENT } from "@/config/theme";
import { useTheme } from "@/contexts/ThemeContext";

interface CostScatterChartProps {
  customers?: Top5Customers[] | null;
  currentWeek: WeekDetails;
  previousWeek: WeekDetails;
  onReady?: (instance: EChartsInstance) => void;
}
/**
 * CostScatterChart
 *
 * @description Displays customer-wise cost distribution using a scatter plot.
 * Each point represents a customer, with cost on Y-axis and category on X-axis.
 * Status-based coloring and tooltip provide insights into week-over-week changes.
 *
 * @param customers - List of customer cost data
 * @param currentWeek - Current week details (label, range, etc.)
 * @param previousWeek - Previous week details for comparison
 *
 * @returns Scatter chart component with legend and tooltip
 */
const CostScatterChart = ({
  customers,
  currentWeek,
  previousWeek,
  onReady,
}: CostScatterChartProps) => {
  const { theme: themeName } = useTheme();
  const normalizedCustomers = useMemo(
    () => (Array.isArray(customers) ? customers : []),
    [customers],
  );
  const hasData = normalizedCustomers.length > 0;

  const chartData = useMemo(
    () => buildScatterChartData(normalizedCustomers),
    [normalizedCustomers, themeName],
  );

  // Get runtime theme colors — recompute on theme switch
  const statusColors = useMemo(
    () => (typeof document !== "undefined" ? getCustomerStatusColors() : CUSTOMER_STATUS_COLORS),
    [themeName],
  );

  const legendGraphic = useMemo(() => {
    const ROW2_TOP = 22; // top for text baseline on row 2
    const CIRCLE_TOP = ROW2_TOP - 1; // circle top ≈ text center (r=6 → center at CIRCLE_TOP+6)
    return [
    // Row 1 — title
    {
      type: "text",
      right: 10,
      top: 4,
      style: {
        text: "Week-over-Week Change (%)",
        fontSize: 11,
        fill: theme.text.tertiary,
        fontWeight: "bold",
      },
    },
    // Row 2 — items right-to-left: Stable → Watch → Review
    // Stable (<5%): text right:10, ~65px wide → left edge ~75; circle right = 75+4 = 79
    {
      type: "circle",
      right: 79,
      top: CIRCLE_TOP,
      shape: { r: 6 },
      style: {
        fill: "transparent",
        stroke: statusColors.Stable.border,
        lineWidth: 2,
      },
    },
    {
      type: "text",
      right: 10,
      top: ROW2_TOP,
      style: { text: "Stable (<5%)", fontSize: 11, fill: theme.text.tertiary },
    },
    // Watch (5–10%): gap 12px → starts at 79+12+12=103; text ~75px; circle right = 103+75+4 = 182
    {
      type: "circle",
      right: 182,
      top: CIRCLE_TOP,
      shape: { r: 6 },
      style: {
        fill: "transparent",
        stroke: statusColors.Watch.border,
        lineWidth: 2,
      },
    },
    {
      type: "text",
      right: 103,
      top: ROW2_TOP,
      style: { text: "Watch (5\u201310%)", fontSize: 11, fill: theme.text.tertiary },
    },
    // Review (>10%): gap 12px → starts at 182+12+12=206; text ~80px; circle right = 206+80+4 = 290
    {
      type: "circle",
      right: 290,
      top: CIRCLE_TOP,
      shape: { r: 6 },
      style: {
        fill: "transparent",
        stroke: statusColors.Review.border,
        lineWidth: 2,
      },
    },
    {
      type: "text",
      right: 206,
      top: ROW2_TOP,
      style: { text: "Review (>10%)", fontSize: 11, fill: theme.text.tertiary },
    },
    ];
  }, [statusColors, themeName]);

  const option = useMemo(() => ({
    backgroundColor: "transparent",
    grid: {
      top: 44,
      right: 24,
      bottom: 90,
      left: 35,
      containLabel: true,
      backgroundColor: "transparent",
    },

    graphic: legendGraphic,

    tooltip: {
      trigger: "item",
      backgroundColor: CHART.tooltipBg,
      borderColor: ACCENT.aqua,
      borderWidth: 1,
      borderRadius: 8,
      textStyle: { color: CHART.tooltipText },
      extraCssText: `box-shadow: ${CHART.tooltipShadow};`,
      confine: true,
      position: (point: number[]) => [point[0], 10],
      formatter: (params: CallbackDataParams) => {
        if (!params.value || !Array.isArray(params.value)) return "";
        const [name, current, percent, previous] = params.value as [
          string,
          number,
          number,
          number,
          keyof typeof CUSTOMER_STATUS_COLORS,
        ];

        return buildTooltipForCharts({
          type: "weekly",
          title: name || "",
          currentLabel: currentWeek?.label || "",
          previousLabel: previousWeek?.label || "",
          currentCost: current || 0,
          previousCost: previous || 0,
          changePercent: percent,
          chartType: "customer",
        });
      },
    },

    xAxis: {
      type: "category",
      data: normalizedCustomers.map((c) => c.customer),
      axisLine: {
        lineStyle: { color: theme.border.subtle, width: 1 },
      },
      axisTick: { show: false },
      axisLabel: {
        color: theme.text.tertiary,
        fontSize: 12,
        interval: 0,
        formatter: (value: string) => {
          const upper = value.toUpperCase();
          return upper.length > 15 ? upper.slice(0, 15) + "..." : upper;
        },
      },
      
    },

    yAxis: {
      type: "value",
      min: 0,
      axisLabel: {
        formatter: (v: number) => `$${ConversionToK(v)}`,
        color: theme.text.tertiary,
        fontSize: 11,
        fontWeight: "500",
      },
      splitLine: {
        lineStyle: {
          color: theme.border.subtle,
          type: "dashed",
          width: 1,
        },
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },

    series: [
      {
        type: "scatter",
        data: chartData,
        symbolSize: 16,
        emphasis: {
          scale: 1.35,
          itemStyle: {
            shadowBlur: 12,
            shadowColor: CHART.emphasisShadow,
          },
        },
      },
    ],
  }), [legendGraphic, normalizedCustomers, chartData, currentWeek, previousWeek, themeName]);

  if (!hasData) {
    return (
      <div className="w-full h-full flex items-center justify-center text-text-tertiary">
        No customers data available
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "transparent" }} className="h-full w-full">
      <ReactECharts
        key={themeName}
        option={option}
        className="h-full w-full"
        opts={{ renderer: "canvas" }}
        onChartReady={onReady}
      />
    </div>
  );
};

export default CostScatterChart;
